import { Property } from "../types/propertyTypes";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../assets/logo.png";
import { getAllPropertyImages } from "./getAllPropertiesImage";

export const generateProfessionalPdfReport = async (
  property: Property,
  reportData: {
    institutionalClient: string;
    clientPobox: string;
    dateOfAssignment: string;
    clientNames: string;
    valuationFirm: string;
    certifiedValuer: string;
    principalCertifiedValuer: string;
    assistantValuerName: string;
    dateOfInspection: string;
    zoningDescription: string;
    servicesDescription: string;
    plotDescription: string;
    improvementsDescription: string;
    remarks: string;
    landRegistrationOfficer: string;
    openMarketValue: number;
    insuranceValue: number;
    forcedSaleValue: number;
    mapPhotos: string[];
    valuationParameters: Array<{ parameter: string; value: string }>;
    potentialAnalysis?: string;
    developmentsDescription?: string;
  }
) => {
  console.log("reportData:---", reportData);

  try {
    // ==========================================
    // 1. Enhance reportData with property info
    // ==========================================
    reportData.openMarketValue = property.openMarketValue || property.valuationDetails?.openMarketValue || 0;
    reportData.insuranceValue = property.insuranceValue || property.valuationDetails?.insuranceValue || 0;
    reportData.forcedSaleValue = property.forcedSaleValue || property.valuationDetails?.forcedSaleValue || 0;
    reportData.dateOfAssignment = property.valuationTaskDetails?.valuationAssignmentDate || reportData.dateOfAssignment;
    reportData.clientNames = property.ownerName || reportData.clientNames;
    reportData.valuationFirm = property.valuationTaskDetails?.regulatoryBody || reportData.valuationFirm;
    reportData.certifiedValuer = property.valuationTaskDetails?.principalValuer || reportData.certifiedValuer;
    reportData.principalCertifiedValuer = property.valuationTaskDetails?.principalValuer || reportData.principalCertifiedValuer;
    reportData.assistantValuerName = property.valuationTaskDetails?.assistantValuer || reportData.assistantValuerName;
    reportData.dateOfInspection = property.valuationDetails?.valuationDate || reportData.dateOfInspection;
    reportData.zoningDescription = property.zoning || reportData.zoningDescription;
    reportData.remarks = property.valuationDetails?.collectorNotes || reportData.remarks;

    const isUganda = !!property?.location?.region || (property as any)?.location?.country === "UG";
const currency = isUganda ? "UGX" : "RWF";

reportData.mapPhotos =
      property.propertyPhotos
        ?.filter((photo) => photo.label.toLowerCase().includes("google earth") || photo.label.toLowerCase().includes("master plan"))
        .map((photo) => photo.url) || reportData.mapPhotos;

    // Services / Amenities description
    reportData.servicesDescription =
      `Electricity: ${property.hasElectricity ? "Yes" : "No"}, Water: ${property.hasWater ? "Yes" : "No"}, ` +
      `Sewage: ${property.hasSewage ? "Yes" : "No"}, Internet: ${property.hasInternet ? "Yes" : "No"}, ` +
      `Parking Space: ${property.hasParkingSpace ? "Yes" : "No"}, ` +
      `Annex Building: ${property.amenitiesData?.hasAnnexBuilding ? "Yes" : "No"}, ` +
      `Outside Kitchen: ${property.amenitiesData?.hasOutsideKitchen ? "Yes" : "No"}, ` +
      `Garden: ${property.amenitiesData?.hasGarden ? "Yes" : "No"}, ` +
      `Paved Parking: ${property.amenitiesData?.hasPavedParking ? "Yes" : "No"}, ` +
      `Water Tank: ${property.buildingComponents?.waterTankDescription || "No"}, ` +
      `PVC Pipes: ${property.buildingComponents?.pvcPipesDescription || "No"}, ` +
      `Wash Taps: ${property.buildingComponents?.washTapsDescription || "No"}, ` +
      `Fixtures/Fittings: ${property.buildingComponents?.fixturesFittingsDescription || "No"}` ||
      reportData.servicesDescription;

    // Plot / Parcel description
    reportData.plotDescription =
      `Plot Number: ${property.plotNumber || "N/A"}, Area: ${property.landParcel?.area || property.landArea || 0} m², ` +
      `Use: ${property.landParcel?.landUse || property.landUse || "N/A"}, Slope: ${property.landParcel?.slopeCategory || property.slopeCategory || "N/A"}, ` +
      `Access to Road: ${property.landParcel?.accessToRoad || property.accessToRoad ? "Yes" : "No"}, ` +
      `Soil Drainage: ${property.buildingComponents?.soilDrainageDescription || "N/A"}, ` +
      `Coordinates: Latitude ${property.location?.coordinates?.latitude || property.latitude || "N/A"}, ` +
      `Longitude ${property.location?.coordinates?.longitude || property.longitude || "N/A"}` ||
      reportData.plotDescription;

    // Improvements / Developments
    reportData.improvementsDescription =
      `Outside Kitchen: ${property.buildingComponents?.outsideKitchenDescription || "N/A"}, ` +
      `Water Tank: ${property.buildingComponents?.waterTankDescription || "N/A"}, ` +
      `Landscaping: ${property.buildingComponents?.landscapingDescription || "N/A"}, ` +
      `Soil Drainage: ${property.buildingComponents?.soilDrainageDescription || "N/A"}, ` +
      `PVC Pipes: ${property.buildingComponents?.pvcPipesDescription || "N/A"}, ` +
      `Wash Taps: ${property.buildingComponents?.washTapsDescription || "N/A"}, ` +
      `Fixtures/Fittings: ${property.buildingComponents?.fixturesFittingsDescription || "N/A"}, ` +
      `Septic Tank: ${property.valuationDetails?.septicTankNumber || 0} units valued at ${currency} ${property.valuationDetails?.septicTankValue || 0}` ||
      reportData.improvementsDescription;

    reportData.developmentsDescription =
      `Main Building: ${property.mainBuilding?.constructionQuality || "N/A"} quality, ` +
      `${property.mainBuilding?.numberOfFloors || 0} floors, Built in ${property.mainBuilding?.constructionYear || property.yearOfConstruction || "N/A"}, ` +
      `Constructed Area: ${property.mainBuilding?.constructedArea || property.constructedArea || 0} m², ` +
      `Bedrooms: ${property.mainBuilding?.bedrooms || property.bedrooms || 0}, ` +
      `Bathrooms: ${property.mainBuilding?.bathrooms || property.bathrooms || 0}, ` +
      `Roof: ${property.mainBuilding?.roofType || "N/A"}, Condition: ${property.mainBuilding?.roofCondition || "N/A"}, ` +
      `Floors: ${property.mainBuilding?.floorCondition || "N/A"}, Walls: ${property.mainBuilding?.wallsCondition || "N/A"}, ` +
      `Windows: ${property.mainBuilding?.windowsCondition || "N/A"}, Doors: ${property.mainBuilding?.doorsCondition || "N/A"}, ` +
      `Maintenance: ${property.mainBuilding?.maintenanceCondition || property.condition || "N/A"}, ` +
      `Annex Area: ${property.valuationDetails?.annexHouseArea || 0} m², ` +
      `Verandah: ${property.valuationDetails?.verandahArea || 0} m², ` +
      `Garden: ${property.valuationDetails?.gardenArea || 0} m², ` +
      `Paved Area: ${property.valuationDetails?.pavedArea || 0} m², ` +
      `Fence: ${property.valuationDetails?.fenceLength || 0} m, ` +
      `Retaining Walls: ${property.valuationDetails?.retainingWallsLength || 0} m, ` +
      `Entrance Gate: ${property.valuationDetails?.entranceGateLength || 0} m` ||
      reportData.developmentsDescription;

    // ==========================================
    // 2. Compact Valuation Parameters
    // ==========================================
    const valuationDetails = property.valuationDetails || {};
    reportData.valuationParameters = [];

    const addParam = (param: string, value: any) => {
      if (value !== undefined && value !== null && value !== 0 && value !== "" && value !== "N/A") {
        reportData.valuationParameters.push({
          parameter: param,
          value: typeof value === "number" ? `RWF ${value.toLocaleString()}` : value.toString(),
        });
      }
    };

    addParam("Land Use", property.landParcel?.landUse || property.landUse);
    addParam("Slope Category", property.landParcel?.slopeCategory || property.slopeCategory);
    addParam("Land Area (m²)", property.landParcel?.area || property.landArea);
    addParam("Land Rate (per m²)", valuationDetails.landRate);
    addParam("Land Value", valuationDetails.landValue);
    addParam("Main House Area (m²)", valuationDetails.mainHouseArea || property.mainBuilding?.constructedArea);
    addParam("Main House Rate (per m²)", valuationDetails.mainHouseRate);
    addParam("Building Value", valuationDetails.buildingValue);
    addParam("Annex House Area (m²)", valuationDetails.annexHouseArea);
    addParam("Annex Rate (per m²)", valuationDetails.annexRate);
    addParam("Fence Length (m)", valuationDetails.fenceLength);
    addParam("Fence Rate (per m)", valuationDetails.fenceRate);
    addParam("Retaining Walls Length (m)", valuationDetails.retainingWallsLength);
    addParam("Retaining Wall Rate (per m)", valuationDetails.retainingWallRate);
    addParam("Entrance Gate Length (m)", valuationDetails.entranceGateLength);
    addParam("Entrance Gate Rate (per m)", valuationDetails.entranceGateRate);
    addParam("Paved Area (m²)", valuationDetails.pavedArea);
    addParam("Paved Area Rate (per m²)", valuationDetails.pavedAreaRate);
    addParam("Garden Area (m²)", valuationDetails.gardenArea);
    addParam("Garden Rate (per m²)", valuationDetails.gardenRate);
    addParam("Verandah Area (m²)", valuationDetails.verandahArea);
    addParam("Verandah Rate (per m²)", valuationDetails.verandahRate);
    addParam("Septic Tank Number", valuationDetails.septicTankNumber);
    addParam("Septic Tank Value (per unit)", valuationDetails.septicTankValue);
    addParam(`Landscaping Value (${currency})`, valuationDetails.landscapingValue);
    addParam("Amenities Value", valuationDetails.amenitiesValue);
    addParam("Depreciation Value", valuationDetails.depreciationValue);
    addParam("Forced Sale Discount (%)", valuationDetails.forcedSaleDiscount);
    addParam("Total Property Value", valuationDetails.totalPropertyValue);

    // ==========================================
    // 3. PDF Document Creation
    // ==========================================
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const footerHeight = 25;
    let yPos = margin;

    // Logo handling
    let logoBase64 = "";
    try {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = logo;
      const canvas = document.createElement("canvas");
      canvas.width = 100;
      canvas.height = 100;
      const ctx = canvas.getContext("2d");
      ctx!.fillStyle = "#FFFFFF";
      ctx!.fillRect(0, 0, canvas.width, canvas.height);
      await new Promise((resolve) => {
        img.onload = () => {
          ctx!.drawImage(img, 0, 0, 100, 100);
          logoBase64 = canvas.toDataURL("image/png");
          resolve(null);
        };
        img.onerror = () => {
          ctx!.fillStyle = "#0066cc";
          ctx!.fillRect(0, 0, 100, 100);
          ctx!.fillStyle = "#FFFFFF";
          ctx!.font = "bold 20px Arial";
          ctx!.textAlign = "center";
          ctx!.textBaseline = "middle";
          ctx!.fillText("VS", 50, 50);
          logoBase64 = canvas.toDataURL("image/png");
          resolve(null);
        };
      });
    } catch (error) {
      console.error("Error loading logo:", error);
    }

    const addLogoHeader = () => {
      if (logoBase64) {
        try {
          doc.addImage(logoBase64, "PNG", margin, 5, 15, 15);
        } catch { }
      }
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(0, 102, 204);
      doc.setLineWidth(0.3);
      doc.line(margin, 22, pageWidth - margin, 22);
      yPos = 30;
    };

    const checkAndAddPage = (neededSpace: number = 30) => {
      if (yPos + neededSpace > pageHeight - footerHeight) {
        doc.addPage();
        addLogoHeader();
        return true;
      }
      return false;
    };

    const addSectionTitle = (title: string) => {
      checkAndAddPage(25);
      yPos += 5;
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(title.toUpperCase(), margin, yPos);
      yPos += 8;
      doc.setDrawColor(0, 102, 204);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
    };

    const addText = (text: string, fontSize: number = 11, lineSpacing: number = 1.15) => {
      const lines = doc.splitTextToSize(text, pageWidth - 2 * margin);
      const lineHeight = fontSize * lineSpacing * 0.35;
      const neededSpace = lines.length * lineHeight;
      checkAndAddPage(neededSpace);
      doc.setFontSize(fontSize);
      doc.text(lines, margin, yPos);
      yPos += lines.length * lineHeight + 5;
    };

    const addBoldText = (text: string, fontSize: number = 11) => {
      checkAndAddPage(15);
      doc.setFontSize(fontSize);
      // doc.setFont("helvetica", "bold");
      doc.text(text, margin, yPos);
      yPos += fontSize * 0.35 + 3;
      doc.setFont("helvetica", "normal");
    };

    const loadImage = (url: string): Promise<string> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/jpeg", 0.8));
        };
        img.onerror = () => resolve("");
        const timestamp = new Date().getTime();
        img.src = `${url}${url.includes("?") ? "&" : "?"}t=${timestamp}`;
      });
    };

    const formatDate = (dateString: string): string => {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
      } catch {
        return dateString;
      }
    };

    const formattedDateOfAssignment = formatDate(reportData.dateOfAssignment);
    const formattedDateOfInspection = formatDate(reportData.dateOfInspection);
    const currentDate = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });

    // ==========================================
    // Cover Page
    // ==========================================
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageWidth, pageHeight, "F");
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(2);
    doc.rect(15, 15, pageWidth - 30, pageHeight - 30);

    let coverYPos = 30;
    const propertyImages = getAllPropertyImages(property);
    const frontViewPhoto = propertyImages.find(
      (img) => img.label.toLowerCase().includes("front") || img.label.toLowerCase().includes("main")
    ) || propertyImages[0];

    if (frontViewPhoto) {
      try {
        const coverImage = await loadImage(frontViewPhoto.url);
        if (coverImage) {
          const imageWidth = pageWidth - 80;
          const imageHeight = 80;
          const imageX = 40;
          const imageY = coverYPos;
          doc.addImage(coverImage, "JPEG", imageX - 1, imageY - 1, imageWidth + 2, imageHeight + 13);
          doc.roundedRect(imageX - 2, imageY - 2, imageWidth + 4, imageHeight + 15, 5, 5, "D");
          coverYPos = imageY + imageHeight + 15;
        }
      } catch { }
    } else {
      coverYPos = 50;
    }

    if (logoBase64) {
      try {
        doc.addImage(logoBase64, "PNG", pageWidth / 2 - 30, coverYPos, 60, 60);
        coverYPos += 50;
      } catch { }
    } else {
      coverYPos += 15;
    }

    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("PROFESSIONAL", pageWidth / 2, coverYPos, { align: "center" });
    coverYPos += 12;
    doc.text("VALUATION REPORT", pageWidth / 2, coverYPos, { align: "center" });
    coverYPos += 25;

    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text(`Property UPI: ${property.upi || "N/A"}`, pageWidth / 2, coverYPos, { align: "center" });
    coverYPos += 12;
    doc.text(
  isUganda
    ? `Location: ${property.location?.district || "N/A"}, ${(property.location as any)?.county || "N/A"}`
    : `Location: ${property.location?.district || "N/A"}, ${property.location?.sector || "N/A"}`,
  pageWidth / 2, coverYPos, { align: "center" }
);
    coverYPos += 12;
    doc.text(`Owner: ${property.ownerName || "N/A"}`, pageWidth / 2, coverYPos, { align: "center" });
    coverYPos += 15;

    doc.setFontSize(14);
    doc.text(`Prepared for: ${reportData.clientNames}`, pageWidth / 2, coverYPos, { align: "center" });
    coverYPos += 12;

    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(`Date: ${currentDate}`, pageWidth / 2, coverYPos, { align: "center" });

    // ==========================================
    // Letter Page
    // ==========================================
    doc.addPage();
    addLogoHeader();

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(formattedDateOfAssignment, pageWidth - margin, yPos, { align: "right" });
    yPos += 15;

    doc.text(reportData.institutionalClient, margin, yPos);
    yPos += 6;
    doc.text(reportData.clientPobox, margin, yPos);
    yPos += 10;

    doc.setFont("helvetica", "bold");
    doc.text(`Re: Valuation certificate of property UPI: ${property.upi || "X/XX/XX/XX/XXXX"}`, margin, yPos);
    yPos += 10;

    doc.setFont("helvetica", "normal");
    doc.text("Dear Sir,", margin, yPos);
    yPos += 10;

    addText(
      `We received instructions from ${reportData.institutionalClient}, ${reportData.clientPobox}, on ${formattedDateOfAssignment}, to inspect the above property of client ${reportData.clientNames}, and advice on its current market value for Mortgage Purposes.`
    );

    addText(
      `We are pleased to submit the valuation report for the above mentioned property in accordance with our Terms of Reference, Limiting Conditions, Basis of Valuation and Remarks; we value the subject property as at today's date as follows:`
    );

    yPos += 5;
    // doc.setFont("helvetica", "bold");
   addText(`i. Open Market Value: ${currency} ${reportData.openMarketValue.toLocaleString()}`);
addText(`ii. Insurance Value: ${currency} ${reportData.insuranceValue.toLocaleString()}`);
addText(`iii. Forced Sale Value: ${currency} ${reportData.forcedSaleValue.toLocaleString()}`);
    doc.setFont("helvetica", "normal");
    yPos += 8;

    addText("Genuinely submitted,");
    yPos += 12;
    addText("For and on behalf of");
    yPos += 8;

    doc.setFontSize(12);
    // doc.setFont("helvetica", "bold");
    addText(reportData.valuationFirm);
    doc.setFontSize(11);
    yPos += 12;
    addText(reportData.assistantValuerName);
    addText("(Assistant Valuer)");
    yPos += 8;
    addText(reportData.principalCertifiedValuer);
    addText("(Principal Valuer)");

    // ==========================================
    // MANAGING DIRECTOR
    // ==========================================
    // doc.addPage();
    // addLogoHeader();
    // doc.setFontSize(16);
    // doc.setFont("helvetica", "bold");
    // doc.setTextColor(0, 0, 0);

  doc.setFontSize(10); // Reduced from 11 to 10
doc.setFont("helvetica", "normal");

// ==========================================
// 1. DEFINITION OF TERMS
// ==========================================
addSectionTitle("1. DEFINITION OF TERMS");
addBoldText("1.1. VALUATION RATIONALE", 11); // Reduced from 12 to 11
addText(
  `The subject property constitutes ${property.landParcel?.landUse?.toLowerCase() || "a residential/commercial"} establishment and accordingly we have adopted well established valuation techniques that include Sales Comparison/Market Approach and Depreciated Replacement Cost (DRC) to value the same.`,
  10 // Reduced from default to 10
);

yPos += 3;
addBoldText("Sales Comparison / Market Approach", 10); // Reduced from default
addText(
  `According to the RICS Valuation – Professional Standards 2014, the Sales Comparison/Market Approach of valuation provides an indication of value estimate by comparing the subject asset with identical or similar assets for which price information is available.`,
  10 // Reduced from default to 10
);

yPos += 3;
addBoldText("Depreciated Replacement Cost (DRC) Method", 10);
addText(
  `The RICS Valuation – Professional Standards 2014, defines DRC as the current cost of replacing an asset with its modern equivalent asset less deductions for physical deterioration and all relevant forms of obsolescence and optimisation.`,
  10
);

yPos += 3;
addBoldText("Cost Approach Method", 10);
addText(
  `The Cost Approach is based on the economic principle that a buyer will pay no more for an asset than the cost to obtain an asset of equal utility, whether by purchase or construction. It is particularly useful when comparable sales data is limited or when valuing specialised properties.`,
  10
);

yPos += 3; // Reduced from 8 to 6
addBoldText("1.2. BASIS OF VALUATION", 11);
addBoldText("Market Value", 10);
addText(
  `According to the RICS Valuation – Professional Standards 2014, Market Value is the estimated amount for which an asset or liability should exchange on the valuation date between a willing buyer and a willing seller in an arm's length transaction after proper marketing and where the parties had each acted knowledgeably, prudently and without compulsion.`,
  10
);

yPos += 3;
addBoldText("Insurance Value", 10);
addText(
  `Insurance Value represents the cost of reinstatement of the buildings and other improvements in new condition at current prices, including professional fees and demolition costs, but excluding land value. It is normally expressed as the cost of replacement as at the date of valuation.`,
  10
);

yPos += 3;
addBoldText("Forced Sale Value", 10);
addText(
  `According to the RICS Valuation – Professional Standards 2014, the term 'forced sale value' must not be used as a distinct basis of value. A 'forced sale' is a description of the situation under which the exchange takes place, not a basis of value. Forced sales arise where there is pressure on a particular vendor to sell at a specific time – for example, because of the need to raise money or to extinguish a liability by a given date.`,
  10
);

yPos += 3;
addText(
  `Consequently, the advice we are to give of the 'forced sale value' is going to be the estimated likely realization price in forced sale circumstances (less than 6 months sale period) and not a basis of value. Typically, this is estimated at 70%–85% of the Open Market Value depending on market conditions and urgency.`,
  10
);

yPos += 3; // Reduced from 8 to 6
addBoldText("1.3. POTENTIAL, HIGHEST AND BEST USE", 11);
addBoldText("1.3.1. POTENTIAL", 10);
addText(
  `This is the difference between the present use of a property and the highest and best use of the same property. It represents the additional value that could be created by changing the current use to a more profitable and legally permissible one.`,
  10
);

yPos += 3;
addBoldText("1.3.2. HIGHEST AND BEST USE", 10);
addText(
  `This is the reasonably probable and legal use of vacant land or an improved property that is physically possible, appropriately supported, financially feasible and that results in the highest value. The four criteria of highest and best use are: legally permissible, physically possible, financially feasible, and maximally productive.`,
  10
);
yPos += 3;
    // ==========================================
    // 2. LIMITING CONDITIONS
    // ==========================================

   addSectionTitle("2. LIMITING CONDITIONS");

    const conditions = [
      "It is advisable that our approval of the form and context of this Report and Valuation should be sought if any alteration or rephrasing part of it is to appear in any published document or circulars.",
      "Where it is stated in the Report that information has been supplied to the Valuer by another party, this information is believed to be reliable but the Valuer can accept no responsibility if this should prove not to be so.",
      "The responsibility of the Valuer in connection with this Report and Valuation is limited to the client and the bank/institution to whom the report is addressed.",
      "The values assessed in this Report are for the subject property as a whole and any allocation of values between parts of the property apply only in the terms, and for the purpose, of this Report. The values assessed should not be used in conjunction with any other assessment, as they may prove incorrect if so used.",
      "Whilst due care is taken to note significant building defects in the course of our inspection, this is a Report and Valuation and not a structural survey and no guarantee is given in respect of rot, termite, pest infestation or other defects whether exposed or unexposed.",
      "This report is only a valuation report but not a surveying, structural, or geotechnical report. We have relied on the available legal land titles, cadastral fiche, and information provided by the client/bank representative.",
      "Where Open Market Values are assessed, they reflect the full contract value and no account is taken of any liability for taxation on sale, transfer fees, legal costs, or other costs involved in effecting a sale.",
      "The Valuation is only valid if in original form, without alteration, and signed and stamped by the Certified Valuer. We do not guarantee the validity or authenticity of any copies that may be presented to any person, organization or entity as being made from the original.",
      "This valuation has been prepared on the basis of information supplied to us and on the assumption that the property is free from any undisclosed encumbrances, easements, rights of way, or other burdens except those disclosed to us.",
      "We have not carried out a measured building survey and have relied on floor areas and dimensions provided by the client or as appearing on the approved architectural plans.",
      "The valuation assumes that the property complies with all relevant planning permissions, building regulations, and statutory requirements unless otherwise stated.",
      "Market conditions can change rapidly. This valuation reflects our opinion of value as at the date of valuation only and we accept no responsibility for subsequent changes in market conditions.",
      "This report and the opinions expressed herein are confidential and intended solely for the use of the addressee named herein. It may not be reproduced or used for any other purpose without our prior written consent."
    ];

    // Set font to regular (not bold) and smaller size
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10); // Reduced from 11 to 10
    
    conditions.forEach((condition, index) => {
      checkAndAddPage(15); // Reduced from 18 to match smaller font
      const number = `${index + 1}. `;
      const textLines = doc.splitTextToSize(condition, pageWidth - 2 * margin - 10);
      
      // Calculate the width of the number for proper alignment
      const numberWidth = doc.getTextWidth(number);
      
      // Draw the number
      doc.text(number, margin, yPos);
      
      // Draw the text starting after the number
      doc.text(textLines, margin + numberWidth + 2, yPos);
      
      // Adjust line height for smaller font
      const lineHeight = 10 * 1.15 * 0.3528; // Updated to match 10pt font
      yPos += textLines.length * lineHeight + 2;
    });

    yPos += 6; // Reduced spacing at the end

    // ==========================================
    // 3. DATE OF INSPECTION & PROPERTY SETTING
    // ==========================================

    addSectionTitle("3. DATE OF INSPECTION");
    addText(`The property was inspected for Valuation purposes on ${formattedDateOfInspection} BY ${reportData.assistantValuerName}.`);

    addSectionTitle("4. PROPERTY SETTING");
    addBoldText("4.1. Location/Locality");
    addText(
      isUganda
  ? `This property is located on Plot ${property.plotNumber || "N/A"} ${property.location?.village || "N/A"}, ${(property.location as any)?.cell || "N/A"}, ${(property.location as any)?.sector || "N/A"} (Subcounty), ${(property.location as any)?.county || "N/A"} (County), ${property.location?.district || "N/A"} District, ${(property.location as any)?.region || "N/A"} Region, Uganda.\n\nBelow is a Google location map for identification purposes only.`
  : `This property is located on Plot ${property.plotNumber || "N/A"} ${property.location?.village || "N/A"} ${property.location?.cell || "N/A"}, ${property.location?.sector || "N/A"}, ${property.location?.district || "N/A"}, and In ${property.location?.province || "N/A"}.\n\nBelow is a Google location map for identification purposes only.`
    );

        if (reportData.mapPhotos?.length) {
  for (let i = 0; i < Math.min(2, reportData.mapPhotos.length); i++) {
    try {
      const mapImg = await loadImage(reportData.mapPhotos[i]);

      if (mapImg) {
        // NARROW: 50% of available width
        const imgWidth = (pageWidth - 2 * margin) * 0.5;
        const imgX = margin + (pageWidth - 2 * margin - imgWidth) / 2; // Center

        const captionHeight = 10;
        const spacing = 10;

        const pageHeight = doc.internal.pageSize.getHeight();
        const bottomLimit = pageHeight - margin - 20;

        const remaining = bottomLimit - yPos;
        let maxImgHeight = remaining - captionHeight - spacing;

        if (maxImgHeight < 25) maxImgHeight = 25;

        // Reduced height to match narrow width
        const imgHeight = Math.min(40, maxImgHeight); // Reduced to 40

        const format = mapImg.includes("png") ? "PNG" : "JPEG";

        doc.addImage(mapImg, format, imgX, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 5;

        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);

        doc.text(
          `Figure ${i + 1}: ${i === 0 ? "Location Map" : "Zoning Map"}`,
          margin + (pageWidth - 2 * margin) / 2,
          yPos,
          { align: "center" }
        );

        yPos += 10;
      }
    } catch (err) {
      console.log("Map image error:", err);
    }
  }
}



    addBoldText("Zoning and regulation");
    addText(reportData.zoningDescription);

    addSectionTitle("4.2. Services and Amenities");
    addText(reportData.servicesDescription);

    const detailRows: [string, string | number][] = [];

    if (property.landParcel) {
      detailRows.push(["Land Area", `${property.landParcel.area || 0} m²`]);
      detailRows.push(["Land Use", property.landParcel.landUse || "N/A"]);
      detailRows.push(["Slope Category", property.landParcel.slopeCategory || "N/A"]);
      detailRows.push(["Access to Road", property.landParcel.accessToRoad ? "Yes" : "No"]);
    }

    if (property.mainBuilding) {
      detailRows.push(["Constructed Area", `${property.mainBuilding.constructedArea || 0} m²`]);
      detailRows.push(["Construction Year", property.mainBuilding.constructionYear || "N/A"]);
      detailRows.push(["Number of Floors", property.mainBuilding.numberOfFloors || 0]);
      detailRows.push(["Construction Quality", property.mainBuilding.constructionQuality || "N/A"]);
      detailRows.push(["Roof Type", property.mainBuilding.roofType || "N/A"]);
      detailRows.push(["Maintenance Condition", property.mainBuilding.maintenanceCondition || "N/A"]);
    }

    if (property.buildingComponents) {
      detailRows.push(["Bedrooms", property.buildingComponents.numberOfBedrooms || 0]);
      detailRows.push(["Bathrooms", property.buildingComponents.numberOfBathrooms || 0]);
      detailRows.push(["Living Rooms", property.buildingComponents.numberOfLivingRooms || 0]);
      detailRows.push(["Kitchens", property.buildingComponents.numberOfKitchens || 0]);
      detailRows.push(["Toilets", property.buildingComponents.numberOfToilets || 0]);
    }

    if (detailRows.length > 0) {
      const estimatedTableHeight = detailRows.length * 10 + 30;
      if (yPos + estimatedTableHeight > pageHeight - footerHeight) {
        addSectionTitle("5. PLOT/PARCEL AND CONSTRUCTION DETAILS");
      }

      try {
        autoTable(doc, {
          startY: yPos,
          head: [["Component", "Details"]],
          body: detailRows,
          theme: "grid",

          styles: {
            fontSize: 5.2,          // 🔥 extremely small
            cellPadding: 0.6,       // 🔥 almost no padding
            lineWidth: 0.03,
            lineColor: [230, 230, 230],
            textColor: [20, 20, 20],
            valign: "middle",
            minCellHeight: 4.5,     // 🔥 very short rows
          },

          headStyles: {
            fillColor: [0, 102, 204],
            textColor: 255,
            fontStyle: "bold",
            fontSize: 5.5,          // 🔥 tiny header
            cellPadding: 0.8,
            minCellHeight: 5,
          },

          alternateRowStyles: { fillColor: [252, 253, 255] },

          margin: { left: margin, right: margin },

          columnStyles: {
            0: { cellWidth: 55, fontStyle: "bold", halign: "left" },
            1: { cellWidth: 70, halign: "left" },
          },
        });

        yPos = (doc as any).lastAutoTable?.finalY
          ? (doc as any).lastAutoTable.finalY + 6
          : yPos + (detailRows.length * 5) + 10;


      } catch (error) {
        console.error("Error creating property details table:", error);
        addText("Property details table could not be generated. Showing values as text:");
        detailRows.forEach(([param, val]) => addText(`${param}: ${val}`));
      }
    }

    // ==========================================
    // Remaining sections (unchanged for brevity)
    // ==========================================
    // 6. POTENTIAL, HIGHEST AND BEST USE ANALYSIS
    // doc.addPage();
    // addLogoHeader();
    addSectionTitle("6. POTENTIAL, HIGHEST AND BEST USE ANALYSIS");
    const potentialText =
      reportData.potentialAnalysis ||
      `According to the size of Plot ${property.plotNumber || "N/A"}, the planning, nature of the neighbourhood, and the condition of the establishments thereon, it is our considered opinion that the property is put to its highest and best use.`;
    addText(potentialText);

    // 7. IMPROVEMENTS/DEVELOPMENTS
    addSectionTitle("7. IMPROVEMENTS/DEVELOPMENTS");
    const devText =
      reportData.developmentsDescription ||
      reportData.improvementsDescription ||
      `The plot is developed with ${property.mainBuilding?.constructionQuality?.toLowerCase() || "modern"} quality ${property.mainBuilding?.numberOfFloors > 1 ? "multi-storey" : "single-storey"} building(s) ${property.amenities?.hasFence ? "fully fenced" : ""} with ${property.buildingComponents?.numberOfBedrooms || 0} bedrooms, ${property.buildingComponents?.numberOfBathrooms || 0} bathrooms.`;
    addText(devText);

    // 8. GENERAL REMARKS
    addSectionTitle("8. GENERAL REMARKS");
    addText(reportData.remarks || "No additional remarks provided.");

    // 9. BASIS/METHOD OF VALUATION
    addSectionTitle("9. BASIS/METHOD OF VALUATION");
    addText(
      "The basis of the valuation is open market value and accordingly we have adopted well established valuation techniques that include the cost method of valuation that has been used on the building improvements and sales comparable/ Market Approach for land."
    );

    // ==========================================
    // VALUATION PARAMETERS – COMPACT TABLE
    // ==========================================
    // doc.addPage();
    // addLogoHeader();
    // yPos = 30;
    addSectionTitle("VALUATION PARAMETERS");
    yPos += 8;

    // Prepare compact table body
    const tableBody: string[][] = reportData.valuationParameters
      .filter((p) => p.value && p.value !== "0" && p.value !== "N/A")
      .map((p) => [p.parameter, p.value]);

    tableBody.push(["OPEN MARKET VALUE", `${currency} ${reportData.openMarketValue.toLocaleString()}`]);
tableBody.push(["INSURANCE VALUE", `${currency} ${reportData.insuranceValue.toLocaleString()}`]);
tableBody.push(["FORCED SALE VALUE", `${currency} ${reportData.forcedSaleValue.toLocaleString()}`]);

    // Compact & professional table
    autoTable(doc, {
      startY: yPos,
      head: [["Parameter", "Value"]],
      body: tableBody,
      theme: "grid",

      styles: {
        fontSize: 6.5,          // 🔥 smaller text
        cellPadding: 1.2,       // 🔥 reduce padding
        lineWidth: 0.05,
        lineColor: [220, 220, 220],
        textColor: [30, 30, 30],
        overflow: "linebreak",
        valign: "middle",
        minCellHeight: 6,       // 🔥 very small row height
      },

      headStyles: {
        fillColor: [0, 102, 204],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 7,            // 🔥 smaller header
        cellPadding: 1.5,
        minCellHeight: 7,
      },

      columnStyles: {
        0: { cellWidth: 70, fontStyle: "bold", halign: "left" },
        1: {
          cellWidth: 70,
          halign: "right",
        },
      },

      alternateRowStyles: { fillColor: [252, 253, 255] },

      margin: { left: margin, right: margin },

      didParseCell: (data) => {
        // Make empty rows super tight
        if (data.cell.raw === "" && data.row.index < tableBody.length - 3) {
          data.cell.styles.lineWidth = 0;
          data.cell.styles.minCellHeight = 3;
          data.cell.styles.cellPadding = 0.5;
        }
      },

      willDrawCell: (data) => {
        // Highlight last 3 rows (values)
        if (data.row.index >= tableBody.length - 3 && data.column.index === 0) {
          data.cell.styles.fillColor = [245, 248, 255];
        }
      },
    });


    yPos = (doc as any).lastAutoTable?.finalY
      ? (doc as any).lastAutoTable.finalY + 15
      : yPos + (tableBody.length * 12) + 20;

    // Photos & Index
    if (propertyImages.length > 0) {
      doc.addPage();
      addLogoHeader();
      yPos = 30;

      addSectionTitle("PROPERTY PHOTOS");
      yPos += 5;

      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      doc.text(`Total Photos: ${propertyImages.length}`, margin, yPos);
      yPos += 10;

      const cols = 2;
      const rows = 3;
      const photosPerPage = cols * rows;
      const horizontalSpacing = 15;
      const verticalSpacing = 65;
      const imgWidth = 80;
      const imgHeight = 45;

      const displayPhoto = async (photo: any, x: number, y: number, photoNumber: number) => {
        try {
          const base64 = await loadImage(photo.url);
          if (base64) {
            doc.setDrawColor(180, 180, 180);
            doc.setLineWidth(0.5);
            doc.rect(x, y, imgWidth, imgHeight);
            doc.roundedRect(x - 2, y - 2, imgWidth + 4, imgHeight + 15, 5, 5, "D");
            doc.addImage(base64, "JPEG", x + 1, y + 1, imgWidth - 2, imgHeight - 2);
            doc.setFontSize(8);
            doc.setTextColor(80, 80, 80);
            const label = photo.label.length > 25 ? photo.label.substring(0, 22) + "..." : photo.label;
            doc.text(label, x + imgWidth / 2, y + imgHeight + 6, { align: "center" });
            doc.setFontSize(7);
            doc.text(`${photoNumber}`, x + 5, y + imgHeight + 6);
          } else {
            doc.setDrawColor(200, 200, 200);
            doc.rect(x, y, imgWidth, imgHeight);
            doc.setFillColor(245, 245, 245);
            doc.rect(x, y, imgWidth, imgHeight, "F");
            doc.setFontSize(9);
            doc.setTextColor(150, 150, 150);
            doc.text("Photo", x + imgWidth / 2, y + imgHeight / 2 - 5, { align: "center" });
            doc.text("Not Available", x + imgWidth / 2, y + imgHeight / 2 + 5, { align: "center" });
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            const label = photo.label.length > 25 ? photo.label.substring(0, 22) + "..." : photo.label;
            doc.text(label, x + imgWidth / 2, y + imgHeight + 6, { align: "center" });
            doc.setFontSize(7);
            doc.text(`${photoNumber}`, x + 5, y + imgHeight + 6);
          }
        } catch (error) {
          console.error(`Error displaying photo ${photoNumber}:`, error);
        }
      };

      let currentIndex = 0;
      let pageCount = 1;

      while (currentIndex < propertyImages.length) {
        if (currentIndex > 0 && currentIndex % photosPerPage === 0) {
          doc.addPage();
          addLogoHeader();
          yPos = 30;
          addSectionTitle(`PROPERTY PHOTOS (PAGE ${pageCount + 1})`);
          yPos += 5;
          doc.setFontSize(11);
          doc.setTextColor(100, 100, 100);
          doc.text(
            `Photos ${currentIndex + 1}-${Math.min(currentIndex + photosPerPage, propertyImages.length)} of ${propertyImages.length}`,
            margin,
            yPos
          );
          yPos += 10;
          pageCount++;
        }

        const photosToShow = Math.min(photosPerPage, propertyImages.length - currentIndex);

        for (let i = 0; i < photosToShow; i++) {
          const photoIndex = currentIndex + i;
          const photo = propertyImages[photoIndex];
          const row = Math.floor(i / cols);
          const col = i % cols;
          const x = margin + col * (imgWidth + horizontalSpacing);
          const y = yPos + row * verticalSpacing;
          await displayPhoto(photo, x, y, photoIndex + 1);
        }

        currentIndex += photosToShow;
        if (currentIndex < propertyImages.length) {
          yPos += verticalSpacing * rows + 15;
        }
      }
    } else {
      doc.addPage();
      addLogoHeader();
      yPos = 30;
      addSectionTitle("PROPERTY PHOTOS");
      addText("No photographs were available for this property.");
    }

    // Footer
    // At the end, after all content is added
    const totalPages = doc.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      // === Footer - Positioned at absolute bottom with fixed safe margin ===
      const footerY = pageHeight - 8;  // Very bottom (12mm from edge)

      // Thin separator line (optional - comment out if you don't want it)
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.1);
      doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);

      // Page number (left)
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(`Page ${i} of ${totalPages}`, margin, footerY);

      // Report identifier (center)
      doc.text(
        `Report: ${property.upi || property.id || "Confidential"}`,
        pageWidth / 2,
        footerY,
        { align: "center" }
      );

      // Date (right)
      doc.text(currentDate, pageWidth - margin, footerY, { align: "right" });
    }

    const fileName = `Valuation_Report_${property.upi || property.id}_${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
    return true;
  } catch (error) {
    console.error("Error generating professional valuation report:", error);
    alert("Failed to generate professional valuation report. Please try again.");
    return false;
  }
};